/* MMC recently viewed safe lazy load 2026-10-08; purchase and finance logic unchanged. */
const registerRecentlyViewed = () => {
    if (!customElements.get('recently-viewed')) {
      class RecentlyViewed extends HTMLElement {
        constructor() {
          super();
          this.fetchAttempts = 0;
        }

        connectedCallback() {
          if (this.started) return;
          // Load a single capped history fragment only when saved products exist.
          // Card images remain lazy-loaded; no scroll/observer timing dependency.
          this.started = true;
          this.init();
        }

        init() {
          this.products = [];
          try {
            const stored = JSON.parse(localStorage.getItem('theme-recently-viewed') || '[]');
            if (Array.isArray(stored)) {
              this.products = [...new Set(stored.map(Number).filter(id => Number.isSafeInteger(id) && id > 0))].slice(0,100);
            }
          } catch (_) { /* Malformed history or restricted storage: omit the shelf. */ }
          this.excludeProduct = Number(this.dataset.excludeProduct);
          this.productLimit = Math.min(14, Math.max(1, Number(this.dataset.productLimit) || 8));
          this.searchUrl = this.dataset.searchUrl;

          if (this.products.length > 0) {
            this.fetchProducts();
          }
        }

        async fetchProducts() {
          try {
            // Exclude specified product from list if it exists
            if (this.excludeProduct && this.products.includes(this.excludeProduct)) {
              this.products.splice(this.products.indexOf(this.excludeProduct), 1);
            }

            // Prepare the search query
            const searchQuery = this.products
              .map((product) => `id:${product}`)
              .slice(0, this.productLimit)
              .join(' OR ');

            if (!searchQuery || !this.isConnected) return;
            const response = await fetch(`${this.searchUrl}&q=${encodeURIComponent(searchQuery)}`);
            if (!response.ok) throw new Error(response.status);

            // Fetch response template and insert sorted product items
            const tmpl = document.createElement('template');
            tmpl.innerHTML = await response.text();

            const el = tmpl.content.querySelector('recently-viewed');
            if (el && el.hasChildNodes()) {
              const productIDs = this.products;
              const ul = el.querySelector('ul.amc-trending__rail, ul.product-grid');
              if (!ul || !this.isConnected) return;

              // Sort list items based on the order of product IDs
              const sortedListItems = productIDs
                .map(id => Array.from(ul.querySelectorAll('li'))
                  .find(li => parseInt(li.getAttribute('data-product-id')) === id))
                .filter(Boolean);

              // Clear and append sorted list items
              ul.innerHTML = '';
              sortedListItems.forEach(li => ul.appendChild(li));

              const pendingScripts = [...el.querySelectorAll('script')].filter(sc => !sc.textContent.includes('data-qr-prev'));
              el.querySelectorAll('script').forEach(sc => sc.remove());
              pendingScripts.forEach(script => script.remove());
              this.innerHTML = el.innerHTML;
              this.setupReliableNavigation();

              // Render inline scripts
              const scripts = pendingScripts;
              scripts.forEach((script) => {
                const newScript = document.createElement('script');
                if (script.src) {
                  if ([...document.scripts].some(existing => existing.src === script.src)) return;
                  newScript.src = script.src;
                  newScript.async = false;
                } else {
                  newScript.textContent = script.textContent;
                }
                document.body.appendChild(newScript); // Or append it to a specific container
              });

              // Load external CSS
              const styles = el.querySelectorAll('link[rel="stylesheet"]');
              styles.forEach((link) => {
                if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(existing => existing.href === link.href)) return;
                const newLink = document.createElement('link');
                newLink.rel = 'stylesheet';
                newLink.href = link.href;
                document.head.appendChild(newLink);
              });

              // Call external functions to render and reinitialize
              window.renderSelects?.();
              window.loadDesktopOnlyTemplates?.();
              window.stickersReinit?.();
            }
          } catch (error) {
            this.fetchAttempts += 1;

            if (this.fetchAttempts > 4) {
              console.log('Recently Viewed section error:', error);
            } else {
              window.setTimeout(() => { if (this.isConnected) this.fetchProducts(); }, Math.min(4000,500*2**(this.fetchAttempts-1))); // Bounded retry backoff
            }
          }
        }

        setupReliableNavigation() {
          const qr = this.querySelector('.amc-quickrow');
          if (qr) {
            const rail = qr.querySelector('.amc-trending__rail');
            const go = d => { const w = rail.firstElementChild?.getBoundingClientRect().width || 240; const max = rail.scrollWidth - rail.clientWidth; let n = rail.scrollLeft + d * (w + 10); if (n > max + 2) n = 0; else if (n < 0) n = max; rail.scrollTo({ left: Math.max(0, n), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); };
            qr.querySelector('[data-qr-prev]')?.addEventListener('click', () => go(-1));
            qr.querySelector('[data-qr-next]')?.addEventListener('click', () => go(1));
            return;
          }
          const sliderComponent = this.querySelector('slider-component');
          const slider = sliderComponent?.querySelector('ul.product-grid');
          const previous = sliderComponent?.querySelector('.slider-button--prev');
          const next = sliderComponent?.querySelector('.slider-button--next');
          const controls = sliderComponent?.querySelector('.slider-buttons');
          if (!sliderComponent || !slider || !previous || !next || !controls) return;

          sliderComponent.classList.add('amc-recently-viewed-slider');
          const hasOverflow = () => slider.scrollWidth > slider.clientWidth + 2;
          const refresh = () => {
            const scrollable = hasOverflow();
            controls.hidden = !scrollable;
            previous.disabled = !scrollable;
            next.disabled = !scrollable;
            slider.classList.toggle('slider--scroll-active', scrollable);
          };
          const move = (direction) => {
            if (!hasOverflow()) return;
            const max = Math.max(0, slider.scrollWidth - slider.clientWidth);
            const card = slider.querySelector('.slider__slide, .grid__item');
            const gap = parseFloat(getComputedStyle(slider).columnGap || getComputedStyle(slider).gap) || 0;
            const step = Math.max(card?.getBoundingClientRect().width + gap || 0, slider.clientWidth * .78);
            let target = slider.scrollLeft + direction * step;
            if (direction > 0 && slider.scrollLeft >= max - 2) target = 0;
            if (direction < 0 && slider.scrollLeft <= 2) target = max;
            slider.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          };
          const bind = (button, direction) => button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            move(direction);
          }, true);
          bind(previous, -1);
          bind(next, 1);

          slider.style.touchAction = 'auto';
          requestAnimationFrame(() => requestAnimationFrame(() => {
            sliderComponent.resetPages?.();
            sliderComponent.update?.();
            refresh();
          }));
          slider.querySelectorAll('img').forEach((image) => {
            if (!image.complete) image.addEventListener('load', refresh, { once: true });
          });
          new ResizeObserver(refresh).observe(slider);
        }
      }

      customElements.define('recently-viewed', RecentlyViewed);
    }
  };
if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',registerRecentlyViewed,{once:true});}else registerRecentlyViewed();
